import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { BottomSheet } from '../../ui/sheets/BottomSheet';
import { authedFetchJson } from '../../utils/api';
import { navigate } from '../../router/router';

// Types V2 - Single quest with multiple objectives
type Objective = {
  id: number;
  title_key: string;
  icon: string;
  action_type: string;
  target: number;
  current: number;
  completed: boolean;
};

type Quest = {
  id: number | null;
  template_id?: number;
  date: string;
  title_key?: string;
  description_key?: string;
  reward_credits?: number;
  icon?: string;
  objectives: Objective[];
  all_completed: boolean;
  can_claim: boolean;
  already_claimed: boolean;
  credits_awarded: number;
  quest_unavailable?: boolean;
};

type WeeklyStatus = {
  days_completed: number;
  required_days: number;
  bonus_credits: number;
  can_claim: boolean;
  already_claimed: boolean;
};

type QuestsData = {
  enabled: boolean;
  date: string;
  quest: Quest | null;
  weekly: WeeklyStatus | null;
};

// LocalStorage key for permanent opt-out
const LS_KEY_NEVER_SHOW = 'aicams_quest_popup_never_show';
const LS_KEY_LAST_SHOWN_DATE = 'aicams_quest_popup_last_shown_date';

function getTodayDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Check if popup should auto-show: blocked by "never show" OR already shown today
function shouldAutoShow(): boolean {
  try {
    if (localStorage.getItem(LS_KEY_NEVER_SHOW) === 'true') return false;
    return localStorage.getItem(LS_KEY_LAST_SHOWN_DATE) !== getTodayDateStr();
  } catch {
    return true;
  }
}

export function markQuestShownToday(): void {
  try { localStorage.setItem(LS_KEY_LAST_SHOWN_DATE, getTodayDateStr()); } catch {}
}

// Check if "never show" is currently set
export function isNeverShowSet(): boolean {
  try {
    return localStorage.getItem(LS_KEY_NEVER_SHOW) === 'true';
  } catch {
    return false;
  }
}

// Set or clear "never show" preference
export function setNeverShow(value: boolean): void {
  try {
    if (value) {
      localStorage.setItem(LS_KEY_NEVER_SHOW, 'true');
    } else {
      localStorage.removeItem(LS_KEY_NEVER_SHOW);
    }
  } catch {}
}

// Icon mapping
function getQuestIcon(icon: string): string {
  const icons: Record<string, string> = {
    chat: '💬',
    eye: '👀',
    video: '🎬',
    star: '⭐',
    fire: '🔥',
    heart: '❤️',
    gift: '🎁',
    trophy: '🏆',
    rocket: '🚀',
    sparkles: '✨',
    crown: '👑',
    gem: '💎',
    lock: '🔓',
    camera: '📸',
    kiss: '💋',
    muscle: '💪',
  };
  return icons[icon] || icons.star;
}

export function QuestSheet(props: {
  open: boolean;
  onClose: () => void;
  autoShow?: boolean;
}) {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<QuestsData | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimingWeekly, setClaimingWeekly] = useState(false);
  const [neverShowChecked, setNeverShowChecked] = useState(false);

  // Load quests
  const loadQuests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authedFetchJson(services, '/api/quests/today');
      if (response.ok) {
        setData(response.data as QuestsData);
      }
    } catch (err) {
      console.error('[QuestSheet] Failed to load quests:', err);
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    if (props.open) {
      loadQuests();
    }
  }, [props.open, loadQuests]);

  // Handle auto-show logic after quest data is loaded
  // Simple rule: close if system disabled, no quest, or quest is already claimed/unavailable
  useEffect(() => {
    if (!props.autoShow || loading || !data) return;
    
    // If system is disabled, close immediately
    if (!data.enabled) {
      props.onClose();
      return;
    }
    
    const quest = data.quest;
    
    // If no quest available, close
    if (!quest) {
      props.onClose();
      return;
    }
    
    // If quest is already claimed or unavailable (legacy), close
    if (quest.already_claimed || quest.quest_unavailable) {
      props.onClose();
      return;
    }
    
    // Otherwise, keep the popup open - user has active quest to complete
  }, [props.autoShow, loading, data, props.onClose]);

  // Claim quest reward
  const claimQuest = async () => {
    if (claiming || !data?.quest?.can_claim) return;
    setClaiming(true);

    try {
      const response = await authedFetchJson(services, '/api/quests/claim', {
        method: 'POST',
        body: JSON.stringify({ date: data.quest.date }),
      });

      if (response.ok && (response.data as any)?.ok) {
        await loadQuests();
        window.dispatchEvent(new Event('aicams-credits-refresh'));
      }
    } catch (err) {
      console.error('[QuestSheet] Failed to claim quest:', err);
    } finally {
      setClaiming(false);
    }
  };

  // Claim weekly bonus
  const claimWeeklyBonus = async () => {
    if (claimingWeekly) return;
    setClaimingWeekly(true);

    try {
      const response = await authedFetchJson(services, '/api/quests/weekly/claim', {
        method: 'POST',
      });

      if (response.ok && (response.data as any)?.ok) {
        await loadQuests();
        window.dispatchEvent(new Event('aicams-credits-refresh'));
      }
    } catch (err) {
      console.error('[QuestSheet] Failed to claim weekly bonus:', err);
    } finally {
      setClaimingWeekly(false);
    }
  };

  // Handle close
  const handleClose = () => {
    // If user checked "don't show automatically", set permanent opt-out
    if (props.autoShow && neverShowChecked) {
      setNeverShow(true);
    }
    props.onClose();
  };

  // Go to tasks page
  const goToTasks = () => {
    handleClose();
    navigate({ name: 'tasks', from: 'quests' });
  };

  if (!props.open) return null;

  const quest = data?.quest;
  const weekly = data?.weekly;
  const objectives = quest?.objectives || [];
  const completedCount = objectives.filter(o => o.completed).length;
  const totalCount = objectives.length;

  return (
    <BottomSheet open={props.open} onClose={handleClose} ariaLabel="Daily Quest">
      <div class="sheetContent" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {/* Compact header */}
        <div class="questCompactHeader">
          <div class="questCompactTitle">
            <span class="questCompactEmoji">🎁</span>
            <span>{t('quest.daily_title', 'Daily Quest')}</span>
          </div>
          {!loading && quest && totalCount > 0 && (
            <div class="questCompactProgress">{completedCount}/{totalCount}</div>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div class="questSheetLoading">
            <div class="spinner" />
          </div>
        )}

        {/* Disabled state */}
        {!loading && data && !data.enabled && (
          <div class="questSheetEmpty">
            {t('quest.disabled', 'Daily quests are currently unavailable.')}
          </div>
        )}

        {/* No quest */}
        {!loading && data?.enabled && !quest && (
          <div class="questSheetEmpty">
            {t('quest.no_quest', 'No quest available today. Check back tomorrow!')}
          </div>
        )}

        {/* Quest with objectives */}
        {!loading && data?.enabled && quest && (
          <div class="questContainer">
            {/* Quest Unavailable (legacy claimed quest) */}
            {quest.quest_unavailable ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
                <div style={{ fontWeight: 900, fontSize: 22, marginBottom: 12, color: 'var(--text-main)' }}>
                  {t('quest.completed_title', 'Quest Completed!')}
                </div>
                <div style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  background: 'rgba(50,205,50,0.2)',
                  padding: '12px 24px',
                  borderRadius: 24,
                  fontWeight: 800,
                  fontSize: 18,
                  color: 'rgba(50,205,50,0.95)',
                }}>
                  +{quest.credits_awarded} {t('quest.credits', 'credits')}
                </div>
                <div style={{ fontSize: 14, opacity: 0.7, marginTop: 20 }}>
                  {t('quest.check_tomorrow', 'Check back tomorrow for a new quest!')}
                </div>
              </div>
            ) : (
              <>
                {/* Quest title */}
                <div class="questTitleSection">
                  <div class="questMainTitle">{quest.icon || '🎯'} {t(`quest.template.${quest.template_id}`, quest.title_key || '')}</div>
                  {quest.description_key && (
                    <div class="questMainDesc">{t(`quest.template.${quest.template_id}.desc`, quest.description_key)}</div>
                  )}
                </div>

                {/* Objectives list */}
                <div class="questObjectivesList">
                  {objectives.map(obj => (
                    <div
                      key={obj.id}
                      class={`questObjectiveCard ${obj.completed ? 'questObjectiveCompleted' : ''}`}
                    >
                      <div class="questObjectiveIcon">{getQuestIcon(obj.icon)}</div>
                      <div class="questObjectiveContent">
                        <div class="questObjectiveTitle">{t(`quest.objective.${obj.id}`, obj.title_key)}</div>
                        <div class="questObjectiveProgress">
                          <div class="questProgressBar">
                            <div
                              class="questProgressFill"
                              style={{
                                width: `${Math.min(100, (obj.current / obj.target) * 100)}%`,
                              }}
                            />
                          </div>
                          <span class="questProgressText">
                            {obj.current}/{obj.target}
                          </span>
                        </div>
                      </div>
                      <div class="questObjectiveStatus">
                        {obj.completed ? (
                          <span class="questObjectiveCheck">✓</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reward section */}
                <div class="questRewardCard">
                  <div class="questRewardBadge">
                    <span class="questRewardEmoji">🎁</span>
                    <span class="questRewardLabel">{t('quest.reward', 'Reward')}:</span>
                    <span class="questRewardAmount">+{quest.reward_credits}</span>
                    <span class="questRewardCreditsLabel">{t('quest.credits', 'credits')}</span>
                  </div>

                  {quest.already_claimed ? (
                    <div class="questClaimedSuccess">
                      <span class="questClaimedCheck">✓</span>
                      <span>{t('quest.claimed', 'Claimed!')}</span>
                    </div>
                  ) : quest.can_claim ? (
                    <button
                      class="questClaimBtn"
                      onClick={claimQuest}
                      disabled={claiming}
                    >
                      {claiming ? (
                        <span>{t('quest.claiming', 'Claiming...')}</span>
                      ) : (
                        <>
                          <span>🎁</span>
                          <span>{t('quest.claim_reward', 'Claim Reward')}</span>
                          <span class="questClaimAmount">+{quest.reward_credits}</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <div class="questNotReadyHint">
                      {t('quest.complete_all', 'Complete all objectives to claim reward')}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Weekly progress */}
        {!loading && weekly && (
          <div class="questWeeklySection">
            <div class="questWeeklyHeader">
              <span class="questWeeklyIcon">🔥</span>
              <span class="questWeeklyTitle">
                {t('quest.weekly_streak', 'Weekly Streak')}: {weekly.days_completed}/{weekly.required_days} {t('quest.days', 'days')}
              </span>
            </div>
            <div class="questWeeklyBar">
              <div
                class="questWeeklyBarFill"
                style={{
                  width: `${Math.min(100, (weekly.days_completed / weekly.required_days) * 100)}%`,
                }}
              />
            </div>
            {weekly.can_claim ? (
              <button
                class="questWeeklyClaimBtn"
                onClick={claimWeeklyBonus}
                disabled={claimingWeekly}
              >
                {claimingWeekly
                  ? t('quest.claiming', 'Claiming...')
                  : `${t('quest.claim_bonus', 'Claim Bonus')} +${weekly.bonus_credits}`}
              </button>
            ) : weekly.already_claimed ? (
              <div class="questWeeklyClaimedText">
                ✓ {t('quest.bonus_claimed', 'Bonus claimed!')}
              </div>
            ) : (
              <div class="questWeeklyHint">
                {t('quest.weekly_hint', 'Complete daily quest for')} {weekly.required_days} {t('quest.days', 'days')} {t('quest.for_bonus', 'to get')} +{weekly.bonus_credits} {t('quest.bonus', 'bonus')}!
              </div>
            )}
          </div>
        )}

        {/* Auto-show controls */}
        {props.autoShow && (
          <label class="questNeverShowLabel">
            <input
              type="checkbox"
              checked={neverShowChecked}
              onChange={(e) => setNeverShowChecked((e.target as HTMLInputElement).checked)}
            />
            <span>{t('quest.dont_show_popup', "Don't show this popup automatically")}</span>
          </label>
        )}
      </div>

      {/* Footer */}
      <div class="sheetFooter" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button class="questViewAllBtn" onClick={goToTasks}>
          {t('quest.view_all_tasks', 'View All Tasks')}
        </button>
        <button 
          class="questCloseBtn" 
          onClick={handleClose}
          style={{
            width: '100%',
            padding: '12px 20px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 10,
            color: 'rgba(255,255,255,0.8)',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {t('btn.close', 'Close')}
        </button>
      </div>
    </BottomSheet>
  );
}

// Export helper to check if auto-show should happen
export { shouldAutoShow as shouldAutoShowQuestSheet };
